/** Max members per bulk-import API request (each request is one DB transaction). */
export const BULK_MEMBER_IMPORT_BATCH_SIZE = 100;

export function chunkArray(items, size = BULK_MEMBER_IMPORT_BATCH_SIZE) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

export async function postBulkImportBatch({ token, body }) {
  const response = await fetch('/api/user-groups-periods/bulk-import-members', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: JSON.stringify(body)
  });

  let data;
  try {
    data = await response.json();
  } catch {
    if (response.status === 413) {
      throw new Error('Import batch is too large. Try a smaller file or contact support.');
    }
    throw new Error('Bulk import failed (invalid server response).');
  }

  if (!response.ok || !data.success) {
    if (response.status === 413) {
      throw new Error('Import batch is too large. The file will be retried in smaller batches.');
    }
    throw new Error(data.message || 'Bulk import batch failed.');
  }

  return data;
}

/**
 * Ask the server which existing-user rows would actually change.
 */
export async function previewBulkOverwriteChanges({
  token,
  groupsPeriodsId,
  members,
  overwriteColumns = null
}) {
  const batches = chunkArray(members);
  const candidates = [];
  let unchangedCount = 0;

  for (const batch of batches) {
    const response = await fetch('/api/user-groups-periods/bulk-import-overwrite-preview', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({
        groups_periods_id: groupsPeriodsId,
        overwrite_columns: Array.isArray(overwriteColumns) ? overwriteColumns : undefined,
        members: batch
      })
    });
    const data = await response.json();
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Could not preview overwrite changes.');
    }
    if (Array.isArray(data.candidates)) {
      candidates.push(...data.candidates);
    }
    unchangedCount += data.unchanged_count || 0;
  }

  return { candidates, unchangedCount };
}

/**
 * Import members in transactional batches to stay under request body limits.
 */
export async function bulkImportMembersInBatches({
  token,
  groupsPeriodsId,
  startDate,
  endDate,
  ignoreDuplicates,
  overwriteExisting = false,
  overwriteColumns = null,
  members
}) {
  const batches = chunkArray(members);
  let importedCount = 0;
  let updatedCount = 0;
  const skipped = [];
  const updated = [];

  for (let index = 0; index < batches.length; index += 1) {
    try {
      const data = await postBulkImportBatch({
        token,
        body: {
          groups_periods_id: groupsPeriodsId,
          start_date: startDate,
          end_date: endDate,
          ignore_duplicates: ignoreDuplicates,
          overwrite_existing: overwriteExisting === true,
          overwrite_columns: Array.isArray(overwriteColumns) ? overwriteColumns : undefined,
          members: batches[index]
        }
      });
      importedCount += data.imported_count || 0;
      updatedCount += data.updated_count || 0;
      if (Array.isArray(data.skipped)) {
        skipped.push(...data.skipped);
      }
      if (Array.isArray(data.updated)) {
        updated.push(...data.updated);
      }
    } catch (error) {
      error.importedBeforeFailure = importedCount + updatedCount;
      error.batchIndex = index;
      error.batchCount = batches.length;
      throw error;
    }
  }

  return {
    importedCount,
    updatedCount,
    skipped,
    updated,
    batchCount: batches.length
  };
}
